import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '../../utils/icons';
import { getImageUrl } from '../../utils/imageUrl';
import { formatPrice } from '../../utils/format';
import { ageLabel } from '../../utils/ageLabel';
import { fitToFrame } from '../../utils/fitImage';
import ProductAnimation from '../primitives/ProductAnimation';

const ROTATE_MS = 6000;

const prefersReducedMotion = () => typeof window !== 'undefined'
  && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * The hero's product stage: one product at a time on a clean plate, with
 * its age and price floating beside it, turning to the next every few
 * seconds. Rotation pauses while the shopper hovers or focuses the stage,
 * while the tab is hidden, and never runs for people who ask for reduced
 * motion. A product with a looping clip plays it while it is on stage
 * (ProductAnimation, the same player as the product grid).
 */
const HeroShowcase = ({ items, t, lang, currencyLabel, showAge }) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const stageRef = useRef(null);
  const count = items.length;

  useEffect(() => {
    if (count < 2 || paused || prefersReducedMotion()) return undefined;
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') setIndex((current) => (current + 1) % count);
    }, ROTATE_MS);
    return () => window.clearInterval(id);
  }, [count, paused]);

  useEffect(() => {
    if (index >= count) setIndex(0);
  }, [count, index]);

  if (count === 0) return null;
  const current = items[Math.min(index, count - 1)];
  const age = showAge ? ageLabel(t, current.product) : null;
  const go = (step) => setIndex((value) => (value + step + count) % count);

  return (
    <div
      className="hero-showcase"
      role="region"
      aria-roledescription={t('home.hero.carousel', 'carousel')}
      aria-label={t('home.hero.collageLabel', 'Featured products')}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div className="hero-showcase__stage" ref={stageRef}>
        {items.map(({ product, image }, slide) => (
          <Link
            key={product.id}
            to={`/products/${product.slug}`}
            className={`hero-showcase__slide${slide === index ? ' is-active' : ''}`}
            aria-hidden={slide === index ? undefined : true}
            tabIndex={slide === index ? undefined : -1}
            aria-label={product.name}
          >
            <img
              src={getImageUrl(image, { size: 'large' })}
              alt=""
              loading={slide === 0 ? 'eager' : 'lazy'}
              onLoad={fitToFrame}
              onError={(event) => {
                if (!event.target.dataset.fallback) {
                  event.target.dataset.fallback = '1';
                  event.target.src = getImageUrl(image);
                }
              }}
            />
            {product.animationUrl && (
              <ProductAnimation
                src={getImageUrl(product.animationUrl)}
                poster={getImageUrl(image, { size: 'large' })}
                hostRef={stageRef}
                active={slide === Math.min(index, count - 1)}
              />
            )}
          </Link>
        ))}
      </div>

      {age && (
        <div className="hero-showcase__float hero-showcase__float--age" aria-hidden="true">
          <span>{t('home.hero.ageLabel', 'Age')}</span>
          <strong key={`age-${current.product.id}`}>{age}</strong>
        </div>
      )}
      <Link to={`/products/${current.product.slug}`} className="hero-showcase__float hero-showcase__float--product" tabIndex={-1} aria-hidden="true">
        <span key={`name-${current.product.id}`}>{current.product.name}</span>
        <strong key={`price-${current.product.id}`}>{formatPrice(current.product.price, { lang })} {currencyLabel}</strong>
      </Link>

      {count > 1 && (
        <div className="hero-showcase__controls">
          <button type="button" className="hero-showcase__arrow" onClick={() => go(-1)} aria-label={t('home.hero.previous', 'Previous product')}>
            <Icon.ChevronLeft size={18} aria-hidden="true" />
          </button>
          <div className="hero-showcase__dots">
            {items.map(({ product }, slide) => (
              <button
                key={product.id}
                type="button"
                className={slide === index ? 'is-active' : ''}
                aria-label={t('home.hero.goTo', `Product ${slide + 1} of ${count}`, { index: slide + 1, count })}
                aria-current={slide === index ? 'true' : undefined}
                onClick={() => setIndex(slide)}
              />
            ))}
          </div>
          <button type="button" className="hero-showcase__arrow" onClick={() => go(1)} aria-label={t('home.hero.next', 'Next product')}>
            <Icon.ChevronRight size={18} aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
};

export default HeroShowcase;
